import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaEdit, FaPlus, FaSpinner, FaSync, FaTag } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { catalogNodeService } from '../../../../services/apiService';
import { freshnessService } from '../../../../services/freshnessService';
import { getApiErrorMessage } from '../../../../utils/api';
import VehicleFreshnessAlerts from '../components/VehicleFreshnessAlerts';

function formatPartLabel(row) {
  if (row.partName) return row.partName;
  if (row.vehicleLabel) return row.vehicleLabel;
  return 'Catalog listing';
}

function EditListingModal({ listing, onClose, onSaved }) {
  const canEditAvailability = ['active', 'inactive', 'paused'].includes(listing.status);
  const [form, setForm] = useState({
    price: listing.price ?? '',
    mrp: listing.mrp ?? '',
    quantity: listing.quantity ?? 1,
    dispatchWindowHours: listing.dispatchWindowHours ?? 48,
    availabilityStatus: listing.availabilityStatus || listing.status || 'active',
    confirmHighPrice: false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    try {
      setSaving(true);
      await catalogNodeService.updateListing(listing.listingId, {
        price: Number(form.price),
        mrp: Number(form.mrp),
        quantity: Number(form.quantity),
        dispatchWindowHours: Number(form.dispatchWindowHours),
        ...(canEditAvailability ? { availabilityStatus: form.availabilityStatus } : {}),
        confirmHighPrice: form.confirmHighPrice,
      });
      toast.success('Listing updated');
      onSaved?.();
      onClose();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'DUPLICATE_LISTING') {
        toast.error(getApiErrorMessage(err, 'You already have an active listing for this part.'));
        return;
      }
      if (err?.response?.status === 409) {
        const ok = window.confirm(getApiErrorMessage(err, 'Price is high. Confirm update?'));
        if (ok) {
          setForm((f) => ({ ...f, confirmHighPrice: true }));
          await catalogNodeService.updateListing(listing.listingId, {
            price: Number(form.price),
            mrp: Number(form.mrp),
            quantity: Number(form.quantity),
            dispatchWindowHours: Number(form.dispatchWindowHours),
            ...(canEditAvailability ? { availabilityStatus: form.availabilityStatus } : {}),
            confirmHighPrice: true,
          });
          toast.success('Listing updated');
          onSaved?.();
          onClose();
          return;
        }
      }
      toast.error(getApiErrorMessage(err, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <h3 className="text-lg font-bold text-slate-900">Edit listing</h3>
        <p className="text-sm text-slate-500">{formatPartLabel(listing)}</p>
        {listing.status === 'pending_catalog' && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
            Queued until catalog node is created — price and quantity can still be updated.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          {[
            ['price', 'Price (₹)'],
            ['mrp', 'MRP (₹)'],
            ['quantity', 'Quantity'],
            ['dispatchWindowHours', 'Dispatch (hours)'],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-xs font-semibold text-slate-600">{label}</label>
              <input
                type="number"
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              />
            </div>
          ))}
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-600">Availability</label>
            {canEditAvailability ? (
              <select
                value={form.availabilityStatus}
                onChange={(e) => setForm((f) => ({ ...f, availabilityStatus: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="inactive">Inactive</option>
              </select>
            ) : (
              <p className="mt-1 text-sm text-slate-600 capitalize">{listing.status?.replace('_', ' ')}</p>
            )}
          </div>
        </div>
        <p className="text-[10px] text-slate-400">
          Condition, catalog node, and OEM photos cannot be changed after publish.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg">
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VendorCatalogListings() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [freshnessMap, setFreshnessMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [actionId, setActionId] = useState(null);

  const loadFreshness = useCallback(async (rows) => {
    const map = {};
    await Promise.all(
      rows
        .filter((r) => r.listingId && ['active', 'paused', 'hidden'].includes(r.status))
        .map(async (r) => {
          try {
            map[r.listingId] = await freshnessService.getStatus(r.listingId);
          } catch {
            /* optional */
          }
        })
    );
    setFreshnessMap(map);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await catalogNodeService.listVendorListings({ limit: 100 });
      const rows = res?.data?.listings || [];
      setListings(rows);
      loadFreshness(rows);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load catalog listings'));
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [loadFreshness]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkSold = async (listingId) => {
    if (!window.confirm('Mark this listing as sold? It will be hidden from buyer search.')) return;
    try {
      setActionId(listingId);
      await freshnessService.markSold(listingId);
      toast.success('Marked as sold');
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not mark sold'));
    } finally {
      setActionId(null);
    }
  };

  const handleConfirmAvailable = async (listingId) => {
    try {
      setActionId(listingId);
      await freshnessService.confirmAvailable(listingId);
      toast.success('Availability confirmed');
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not confirm'));
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-4">
      <VehicleFreshnessAlerts />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Catalog listings</h3>
          <p className="text-sm text-slate-500">Quick-list parts on 7-layer catalog nodes</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm"
          >
            <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => navigate('/vendor/dashboard/listings/new')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold"
          >
            <FaPlus /> Quick list
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 flex items-center gap-2">
          <FaSpinner className="animate-spin" /> Loading…
        </p>
      ) : listings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
          No catalog listings yet. Use Quick list to publish in under 60 seconds.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Part</th>
                <th className="px-4 py-3">Condition</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Qty</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Freshness</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {listings.map((row) => (
                <tr key={row.listingId} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <span className="font-medium block">{formatPartLabel(row)}</span>
                    {row.vehicleLabel && row.partName && (
                      <span className="text-xs text-slate-500">{row.vehicleLabel}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{row.condition}</td>
                  <td className="px-4 py-3">₹{row.price}</td>
                  <td className="px-4 py-3">{row.quantity}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100">{row.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {freshnessMap[row.listingId] ? (
                      <span title="Confidence score from view activity">
                        {freshnessMap[row.listingId].confidencePercent}% conf
                        {freshnessMap[row.listingId].pendingPingSince && (
                          <span className="block text-amber-700">Ping pending</span>
                        )}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {freshnessMap[row.listingId]?.pendingPingSince && row.status !== 'sold' && (
                      <button
                        type="button"
                        disabled={actionId === row.listingId}
                        onClick={() => handleConfirmAvailable(row.listingId)}
                        className="text-amber-700 hover:text-amber-900 text-xs font-semibold inline-flex items-center gap-1"
                        title="Confirm still available"
                      >
                        <FaCheck /> Still available
                      </button>
                    )}
                    {['active', 'paused', 'hidden'].includes(row.status) && (
                      <button
                        type="button"
                        disabled={actionId === row.listingId}
                        onClick={() => handleMarkSold(row.listingId)}
                        className="text-slate-600 hover:text-slate-900 text-xs font-semibold inline-flex items-center gap-1"
                        title="Mark sold (convenience — does not affect freshness engine)"
                      >
                        <FaTag /> Mark sold
                      </button>
                    )}
                    {['active', 'paused', 'inactive', 'pending_catalog', 'pending_admin'].includes(
                      row.status
                    ) && (
                      <button
                        type="button"
                        onClick={() => setEditTarget(row)}
                        className="text-emerald-700 hover:text-emerald-900"
                        title="Edit price / stock"
                      >
                        <FaEdit />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editTarget && (
        <EditListingModal
          listing={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
